﻿import { hasDataPage, qs } from "../core/dom";
import { highlightCode, preloadShiki } from "../lib/shiki-highlighter";

let sqlRenderVersion = 0;
let outputRenderVersion = 0;

export async function initSqlToEntityPage() {
    if (!hasDataPage("sql-to-entity")) return;

    const page = {
        sqlEditor: qs("[data-sql-editor]"),
        sqlInput: qs("[data-sql-input]"),
        sqlHighlight: qs("[data-sql-highlight]"),
        csharpOutput: qs("[data-csharp-output]"),
        generateBtn: qs("[data-generate-entity]"),
        loadExampleBtn: qs("[data-load-example]"),
        clearBtn: qs("[data-clear-all]"),
        copyBtn: qs("[data-copy-output]"),
        downloadBtn: qs("[data-download-output]"),

        namespaceInput: qs("[data-namespace]"),
        classNameInput: qs("[data-class-name]"),

        useDataAnnotations: qs("[data-use-data-annotations]"),
        includeTableAttr: qs("[data-include-table-attr]"),
        includeKeyAttr: qs("[data-include-key-attr]"),
        includeColumnAttr: qs("[data-include-column-attr]"),
        includeMaxLengthAttr: qs("[data-include-maxlength-attr]"),

        nullableReferenceTypes: qs("[data-nullable-reference-types]"),
        useInitSetters: qs("[data-use-init-setters]"),
        convertToPascal: qs("[data-convert-to-pascal]"),
        emitRequiredForNotNull: qs("[data-emit-required-for-not-null]"),

        includeSummary: qs("[data-include-summary]"),
        includeDefaultValues: qs("[data-include-default-values]"),
        includeSchemaInTable: qs("[data-include-schema-in-table]"),

        generatorMode: "create-table",
        modeTabs: Array.from(document.querySelectorAll("[data-sql-generator-mode]")),
        modeHint: qs("[data-sql-mode-hint]"),
        outputStyle: qs("[data-sql-output-style]"),
        nullableReference: qs("[data-sql-nullable-reference]"),
    };

    bindEvents(page);

    await preloadShiki();
    await renderInitialState(page);
}

function bindEvents(page) {
    page.loadExampleBtn?.addEventListener("click", async () => loadExample(page));
    page.generateBtn?.addEventListener("click", async () => generate(page));
    page.clearBtn?.addEventListener("click", async () => clearAll(page));
    page.copyBtn?.addEventListener("click", async () => copyOutput(page));
    page.downloadBtn?.addEventListener("click", () => downloadOutput(page));

    page.sqlInput?.addEventListener("input", debounce(async () => {
        autoResizeEditor(page);
        await renderSqlInput(page);
        await generate(page);
    }, 120));

    page.modeTabs.forEach(tab => {
        tab.addEventListener("click", () => {
            setSqlGeneratorMode(page, tab.dataset.sqlGeneratorMode);
        });
    });

    page.sqlInput?.addEventListener("scroll", () => syncEditorScroll(page));
    page.sqlInput?.addEventListener("keydown", (e) => handleEditorTab(e));

    [
        page.namespaceInput,
        page.classNameInput,
        page.useDataAnnotations,
        page.includeTableAttr,
        page.includeKeyAttr,
        page.includeColumnAttr,
        page.includeMaxLengthAttr,
        page.nullableReferenceTypes,
        page.useInitSetters,
        page.convertToPascal,
        page.emitRequiredForNotNull,
        page.includeSummary,
        page.includeDefaultValues,
        page.includeSchemaInTable,

        // SELECT Query / DTO options
        page.outputStyle,
        page.nullableReference
    ].forEach(el => {
        if (!el) return;
        const eventName = el.type === "checkbox" ? "change" : "input";
        el.addEventListener(eventName, () => generate(page));
    });

    document.addEventListener("click", (e) => {
        const toggle = e.target.closest("[data-theme-toggle]");
        if (!toggle) return;

        setTimeout(async () => {
            await renderSqlInput(page);
            const current = page.csharpOutput?.dataset.rawCode || "";
            await renderHighlightedOutput(page, current || "// Your generated entity will appear here.");
        }, 60);
    });
}

function setSqlGeneratorMode(page, mode) {
    page.generatorMode = mode || "create-table";

    page.modeTabs.forEach(tab => {
        tab.classList.toggle("active", tab.dataset.sqlGeneratorMode === page.generatorMode);
    });

    if (page.modeHint) {
        page.modeHint.textContent = page.generatorMode === "select-query"
            ? "Generate a C# DTO class from a SQL SELECT query result."
            : "Generate a C# entity class from a SQL Server CREATE TABLE statement.";
    }

    if (page.classNameInput) {
        const current = page.classNameInput.value?.trim();

        if (page.generatorMode === "select-query" && (!current || current === "GeneratedEntity")) {
            page.classNameInput.value = "QueryResultDto";
        }

        if (page.generatorMode === "create-table" && current === "QueryResultDto") {
            page.classNameInput.value = "";
        }
    }

    generate(page);
}

async function renderInitialState(page) {
    autoResizeEditor(page);
    await renderSqlInput(page);
    await renderHighlightedOutput(page, getEmptyOutputMessage(page));
}

async function generate(page) {
    const sql = page.sqlInput.value.trim();

    if (!sql) {
        await renderHighlightedOutput(page, getEmptyOutputMessage(page));
        return;
    }

    try {
        let csharp = "";

        if (page.generatorMode === "select-query") {
            csharp = generateDtoFromSelect(sql, getSelectDtoOptions(page));
        } else {
            const parsed = parseCreateTable(sql);
            csharp = generateEntityCode(parsed, getOptions(page));
        }

        await renderHighlightedOutput(page, csharp);
    } catch (error) {
        const modeLabel = page.generatorMode === "select-query"
            ? "SELECT query"
            : "CREATE TABLE";

        await renderHighlightedOutput(page, `// Failed to parse ${modeLabel}\n// ${error.message}`);
    }
}

function getEmptyOutputMessage(page) {
    return page.generatorMode === "select-query"
        ? "// Paste a SELECT query to generate a C# DTO."
        : "// Paste a CREATE TABLE script to generate a C# entity.";
}

function getSelectDtoOptions(page) {
    return {
        namespace: page.namespaceInput.value.trim() || "MyApp.Entities",
        className: page.classNameInput.value.trim() || "QueryResultDto",
        outputStyle: page.outputStyle?.value || "class",
        nullableReferenceTypes: page.nullableReference?.checked ?? page.nullableReferenceTypes?.checked ?? true,
        convertToPascal: page.convertToPascal?.checked ?? true,
        useInitSetters: page.useInitSetters?.checked ?? false,
        includeSummary: page.includeSummary?.checked ?? true
    };
}

function getOptions(page) {
    return {
        namespace: page.namespaceInput.value.trim() || "MyApp.Entities",
        classNameOverride: page.classNameInput.value.trim(),
        useDataAnnotations: page.useDataAnnotations.checked,
        includeTableAttr: page.includeTableAttr.checked,
        includeKeyAttr: page.includeKeyAttr.checked,
        includeColumnAttr: page.includeColumnAttr.checked,
        includeMaxLengthAttr: page.includeMaxLengthAttr.checked,
        nullableReferenceTypes: page.nullableReferenceTypes.checked,
        useInitSetters: page.useInitSetters.checked,
        convertToPascal: page.convertToPascal.checked,
        emitRequiredForNotNull: page.emitRequiredForNotNull.checked,
        includeSummary: page.includeSummary.checked,
        includeDefaultValues: page.includeDefaultValues.checked,
        includeSchemaInTable: page.includeSchemaInTable.checked
    };
}

async function renderSqlInput(page) {
    if (!page.sqlHighlight || !page.sqlInput) return;

    const currentVersion = ++sqlRenderVersion;
    const code = page.sqlInput.value || " ";

    const html = await highlightCode(code, "sql");

    if (currentVersion !== sqlRenderVersion) return;

    page.sqlHighlight.innerHTML = html;
    syncEditorScroll(page);
}

async function renderHighlightedOutput(page, code) {
    if (!page.csharpOutput) return;

    const currentVersion = ++outputRenderVersion;
    page.csharpOutput.dataset.rawCode = code;

    const html = await highlightCode(code, "csharp");

    if (currentVersion !== outputRenderVersion) return;

    page.csharpOutput.innerHTML = html;
}

function syncEditorScroll(page) {
    const textarea = page.sqlInput;
    const highlightHost = page.sqlHighlight;

    if (!textarea || !highlightHost) return;

    highlightHost.scrollTop = textarea.scrollTop;
    highlightHost.scrollLeft = textarea.scrollLeft;
}

function handleEditorTab(e) {
    if (e.key !== "Tab") return;

    e.preventDefault();

    const textarea = e.currentTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;

    textarea.value = value.substring(0, start) + "    " + value.substring(end);
    textarea.selectionStart = textarea.selectionEnd = start + 4;

    textarea.dispatchEvent(new Event("input", { bubbles: true }));
}

function parseCreateTable(sql) {
    const cleaned = sql
        .replace(/\r/g, "")
        .replace(/^\s*GO\s*$/gim, "")
        .trim();

    const createTableMatch = cleaned.match(/CREATE\s+TABLE\s+([^\(]+)\(/i);
    if (!createTableMatch) {
        throw new Error("CREATE TABLE statement could not be detected.");
    }

    const fullTableName = createTableMatch[1].trim();
    const { schema, tableName } = parseTableName(fullTableName);

    const createTableStart = cleaned.search(/CREATE\s+TABLE/i);
    if (createTableStart < 0) {
        throw new Error("CREATE TABLE section could not be located.");
    }

    const createTableSql = cleaned.slice(createTableStart);
    const firstParen = createTableSql.indexOf("(");
    const matchingParenIndex = findMatchingParenIndex(createTableSql, firstParen);

    if (firstParen < 0 || matchingParenIndex < 0) {
        throw new Error("Table definition block could not be parsed.");
    }

    const body = createTableSql.slice(firstParen + 1, matchingParenIndex);
    const parts = splitSqlDefinitions(body);

    const columns = [];
    const tableLevelPrimaryKeys = [];

    for (const rawPart of parts) {
        const part = rawPart.trim();
        if (!part) continue;

        if (/^(CONSTRAINT|PRIMARY\s+KEY|UNIQUE|FOREIGN\s+KEY|CHECK)/i.test(part)) {
            const pkColumns = extractPrimaryKeyColumns(part);
            if (pkColumns.length) {
                tableLevelPrimaryKeys.push(...pkColumns);
            }
            continue;
        }

        const column = parseColumn(part);
        if (column) {
            columns.push(column);
        }
    }

    const normalizedPk = new Set(tableLevelPrimaryKeys.map(x => normalizeName(x)));

    columns.forEach(col => {
        if (col.inlinePrimaryKey || normalizedPk.has(normalizeName(col.name))) {
            col.isPrimaryKey = true;
        }
    });

    if (!columns.some(x => x.isPrimaryKey)) {
        const idCol = columns.find(x => normalizeName(x.name) === "id");
        if (idCol) {
            idCol.isPrimaryKey = true;
        }
    }

    applyAlterTableDefaults(cleaned, schema, tableName, columns);

    return {
        schema,
        tableName,
        columns
    };
}

function parseTableName(fullName) {
    const cleaned = fullName.replace(/\[|\]/g, "").trim();
    const parts = cleaned.split(".");
    if (parts.length >= 2) {
        return {
            schema: parts[parts.length - 2],
            tableName: parts[parts.length - 1]
        };
    }

    return {
        schema: "dbo",
        tableName: cleaned
    };
}

function findMatchingParenIndex(text, openIndex) {
    if (openIndex < 0) return -1;

    let depth = 0;
    for (let i = openIndex; i < text.length; i++) {
        const ch = text[i];
        if (ch === "(") depth++;
        else if (ch === ")") {
            depth--;
            if (depth === 0) {
                return i;
            }
        }
    }

    return -1;
}

function splitSqlDefinitions(body) {
    const parts = [];
    let current = "";
    let depth = 0;

    for (let i = 0; i < body.length; i++) {
        const ch = body[i];

        if (ch === "(") depth++;
        if (ch === ")") depth--;

        if (ch === "," && depth === 0) {
            parts.push(current);
            current = "";
            continue;
        }

        current += ch;
    }

    if (current.trim()) {
        parts.push(current);
    }

    return parts;
}

function parseColumn(part) {
    const match = part.match(
        /^\s*(\[[^\]]+\]|[A-Za-z0-9_]+)\s+(\[[^\]]+\]|[A-Za-z0-9_]+)(\s*\(([^)]+)\))?(.*)$/i
    );

    if (!match) return null;

    const rawName = match[1];
    const rawSqlType = match[2];
    const typeArgs = (match[4] || "").trim();
    const rest = (match[5] || "").trim();

    const columnName = rawName.replace(/\[|\]/g, "").trim();
    const sqlType = rawSqlType.replace(/\[|\]/g, "").trim().toLowerCase();

    const isNullable = !/\bNOT\s+NULL\b/i.test(rest);
    const inlinePrimaryKey = /\bPRIMARY\s+KEY\b/i.test(rest);
    const isIdentity = /\bIDENTITY\s*\(/i.test(rest);

    let length = null;
    let precision = null;
    let scale = null;

    if (typeArgs) {
        const args = typeArgs.split(",").map(x => x.trim());
        if (args.length === 1) {
            length = args[0];
        } else if (args.length >= 2) {
            precision = args[0];
            scale = args[1];
        }
    }

    const defaultInlineMatch = rest.match(/\bDEFAULT\b\s*\((.*?)\)/i);

    return {
        name: columnName,
        sqlType,
        isNullable,
        inlinePrimaryKey,
        isPrimaryKey: false,
        isIdentity,
        length,
        precision,
        scale,
        defaultValueSql: defaultInlineMatch ? defaultInlineMatch[1] : null
    };
}

function extractPrimaryKeyColumns(part) {
    const normalized = part
        .replace(/\r/g, " ")
        .replace(/\n/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const match = normalized.match(/PRIMARY\s+KEY(?:\s+CLUSTERED|\s+NONCLUSTERED)?\s*\((.*?)\)/i);
    if (!match) return [];

    return match[1]
        .split(",")
        .map(x =>
            x
                .replace(/\[|\]/g, "")
                .replace(/\bASC\b|\bDESC\b/gi, "")
                .trim()
        )
        .filter(Boolean);
}

function applyAlterTableDefaults(sql, schema, tableName, columns) {
    const normalizedSchema = normalizeName(schema);
    const normalizedTable = normalizeName(tableName);

    const regex =
        /ALTER\s+TABLE\s+\[(?<schema>[^\]]+)\]\.\[(?<table>[^\]]+)\]\s+ADD\s+CONSTRAINT\s+\[(?<constraint>[^\]]+)\]\s+DEFAULT\s*\((?<value>[\s\S]*?)\)\s+FOR\s+\[(?<column>[^\]]+)\]/gi;

    let match;
    while ((match = regex.exec(sql)) !== null) {
        const matchSchema = normalizeName(match.groups?.schema || "");
        const matchTable = normalizeName(match.groups?.table || "");
        const columnName = match.groups?.column?.trim();
        const value = match.groups?.value?.trim();

        if (matchSchema !== normalizedSchema || matchTable !== normalizedTable || !columnName) {
            continue;
        }

        const col = columns.find(x => normalizeName(x.name) === normalizeName(columnName));
        if (col && !col.defaultValueSql) {
            col.defaultValueSql = value || null;
        }
    }
}

function autoResizeEditor(page) {
    const textarea = page.sqlInput;
    const highlight = page.sqlHighlight;

    if (!textarea || !highlight) return;

    // reset height
    textarea.style.height = "auto";

    const newHeight = textarea.scrollHeight;

    textarea.style.height = newHeight + "px";
    highlight.style.height = newHeight + "px";
}

function generateEntityCode(parsed, options) {
    const className = options.classNameOverride
        ? options.classNameOverride
        : (options.convertToPascal ? toPascalCase(parsed.tableName) : parsed.tableName);

    const usings = new Set();
    const lines = [];

    if (options.useDataAnnotations) {
        usings.add("using System.ComponentModel.DataAnnotations;");
        usings.add("using System.ComponentModel.DataAnnotations.Schema;");
    }

    if (parsed.columns.some(x => requiresSystemUsing(x.sqlType))) {
        usings.add("using System;");
    }

    if (options.includeSummary) {
        lines.push("// <auto-generated>");
        lines.push("// Generated by T-SQL to EF Entity tool.");
        lines.push("// </auto-generated>");
        lines.push("");
    }

    if (options.nullableReferenceTypes) {
        lines.push("#nullable enable");
        lines.push("");
    }

    Array.from(usings).sort().forEach(u => lines.push(u));
    if (usings.size) lines.push("");

    lines.push(`namespace ${options.namespace};`);
    lines.push("");

    if (options.useDataAnnotations && options.includeTableAttr) {
        if (options.includeSchemaInTable && parsed.schema) {
            lines.push(`[Table("${parsed.tableName}", Schema = "${parsed.schema}")]`);
        } else {
            lines.push(`[Table("${parsed.tableName}")]`);
        }
    }

    lines.push(`public class ${className}`);
    lines.push("{");

    for (const col of parsed.columns) {
        const propertyName = options.convertToPascal ? toPascalCase(col.name) : col.name;
        const mapped = mapSqlTypeToCSharp(col.sqlType);
        const csharpType = resolvePropertyType(mapped, col, options);

        if (options.useDataAnnotations) {
            if (options.includeKeyAttr && col.isPrimaryKey) {
                lines.push("    [Key]");
            }

            if (col.isIdentity) {
                lines.push("    [DatabaseGenerated(DatabaseGeneratedOption.Identity)]");
            }

            if (options.includeColumnAttr) {
                lines.push(`    [Column("${col.name}")]`);
            }

            if (options.includeMaxLengthAttr && shouldEmitMaxLength(col)) {
                lines.push(`    [MaxLength(${normalizeLength(col.length)})]`);
            }
        }

        if (col.defaultValueSql && options.includeDefaultValues) {
            lines.push(`    // Default: ${col.defaultValueSql}`);
        }

        if (col.precision && col.scale) {
            lines.push(`    // SQL Type Detail: ${col.sqlType}(${col.precision}, ${col.scale})`);
        }

        const requiredKeyword = shouldUseRequired(mapped, col, options) ? "required " : "";
        const setter = options.useInitSetters ? "set;" : "set;";

        lines.push(`    public ${requiredKeyword}${csharpType} ${propertyName} { get; ${setter} }`);
        lines.push("");
    }

    if (lines[lines.length - 1] === "") {
        lines.pop();
    }

    lines.push("}");

    return lines.join("\n");
}

function requiresSystemUsing(sqlType) {
    const type = sqlType.toLowerCase();
    return [
        "date",
        "datetime",
        "datetime2",
        "smalldatetime",
        "time",
        "uniqueidentifier"
    ].includes(type);
}

function mapSqlTypeToCSharp(sqlType) {
    const type = sqlType.toLowerCase();

    switch (type) {
        case "int": return { type: "int", reference: false };
        case "bigint": return { type: "long", reference: false };
        case "smallint": return { type: "short", reference: false };
        case "tinyint": return { type: "byte", reference: false };
        case "bit": return { type: "bool", reference: false };
        case "decimal":
        case "numeric":
        case "money":
        case "smallmoney":
            return { type: "decimal", reference: false };
        case "float": return { type: "double", reference: false };
        case "real": return { type: "float", reference: false };
        case "date":
        case "datetime":
        case "datetime2":
        case "smalldatetime":
            return { type: "DateTime", reference: false };
        case "time":
            return { type: "TimeSpan", reference: false };
        case "uniqueidentifier":
            return { type: "Guid", reference: false };
        case "binary":
        case "varbinary":
        case "image":
            return { type: "byte[]", reference: true, array: true };
        case "char":
        case "nchar":
        case "varchar":
        case "nvarchar":
        case "text":
        case "ntext":
        case "xml":
            return { type: "string", reference: true };
        default:
            return { type: "string", reference: true };
    }
}

function resolvePropertyType(mapped, col, options) {
    if (mapped.reference) {
        if (mapped.type === "string") {
            return options.nullableReferenceTypes
                ? (col.isNullable ? "string?" : "string")
                : "string";
        }

        if (mapped.array) {
            return options.nullableReferenceTypes && col.isNullable ? "byte[]?" : "byte[]";
        }

        return mapped.type;
    }

    return col.isNullable ? `${mapped.type}?` : mapped.type;
}

function shouldEmitMaxLength(col) {
    if (!col.length) return false;
    const normalized = String(col.length).toLowerCase();
    if (normalized === "max") return false;
    return !isNaN(Number(normalized));
}

function normalizeLength(length) {
    return Number(length);
}

function shouldUseRequired(mapped, col, options) {
    return options.nullableReferenceTypes
        && options.emitRequiredForNotNull
        && mapped.type === "string"
        && !col.isNullable;
}

function toPascalCase(value) {
    const cleaned = value.replace(/\[|\]/g, "").trim();

    if (!cleaned.includes("_") && !cleaned.includes(" ")) {
        return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    }

    return cleaned
        .split(/[_\s]+/)
        .filter(Boolean)
        .map(x => x.charAt(0).toUpperCase() + x.slice(1).toLowerCase())
        .join("");
}

function normalizeName(value) {
    return value.replace(/\[|\]/g, "").trim().toLowerCase();
}

async function copyOutput(page) {
    const text = page.csharpOutput?.dataset.rawCode || "";
    await navigator.clipboard.writeText(text);
}

function downloadOutput(page) {
    const content = page.csharpOutput?.dataset.rawCode || "";
    const className = page.classNameInput.value.trim()
        || (page.generatorMode === "select-query" ? "QueryResultDto" : "GeneratedEntity");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${className}.cs`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(url), 500);
}

async function clearAll(page) {
    page.sqlInput.value = "";
    page.classNameInput.value = "";
    await renderSqlInput(page);
    await renderHighlightedOutput(page, getEmptyOutputMessage(page));
}

async function loadExample(page) {
    if (page.generatorMode === "select-query") {
        page.sqlInput.value = getSelectQueryExample();

        if (page.classNameInput && !page.classNameInput.value.trim()) {
            page.classNameInput.value = "ProformaLineDto";
        }

        autoResizeEditor(page);
        await renderSqlInput(page);
        await generate(page);
        return;
    }

    page.sqlInput.value = `
USE [DataBase]
GO

SET ANSI_NULLS ON
GO

SET QUOTED_IDENTIFIER ON
GO

CREATE TABLE [dbo].[VirtualPos](
	[Id] [int] IDENTITY(1,1) NOT NULL,
	[Status] [int] NOT NULL,
	[CreatedAt] [datetime] NOT NULL,
	[PaymentAt] [datetime] NULL,
	[ViewedAt] [datetime] NULL,
	[Reference] [varchar](150) NULL,
	[TranType] [varchar](150) NULL,
	[Amount] [decimal](18, 2) NULL,
	[BrutAmount] [decimal](18, 2) NULL,
	[Installment] [int] NULL,
	[OrderId] [varchar](150) NULL,
	[CompanyId] [int] NULL,
	[Pos] [int] NULL,
	[VPos] [int] NULL,
	[Currency] [int] NULL,
	[Rnd] [varchar](250) NULL,
	[Hash] [varchar](250) NULL,
	[CardHolder] [varchar](250) NULL,
	[CardMasked] [varchar](250) NULL,
	[CardHash] [varchar](max) NULL,
	[Provision] [varchar](250) NULL,
	[RRN] [varchar](250) NULL,
	[Auth] [varchar](250) NULL,
	[FormRequest] [varchar](max) NULL,
	[PaymentId] [varchar](250) NULL,
	[ErrorMsg] [varchar](500) NULL,
	[IpAddress] [varchar](250) NULL,
	[NormalizedIpAddress] [varchar](50) NULL,
	[PaymentKey] [varchar](800) NULL,
 CONSTRAINT [PK_VirtualPos] PRIMARY KEY CLUSTERED 
(
	[Id] ASC
)WITH (PAD_INDEX = OFF, STATISTICS_NORECOMPUTE = OFF, IGNORE_DUP_KEY = OFF, ALLOW_ROW_LOCKS = ON, ALLOW_PAGE_LOCKS = ON, FILLFACTOR = 100, OPTIMIZE_FOR_SEQUENTIAL_KEY = OFF) ON [PRIMARY]
) ON [PRIMARY] TEXTIMAGE_ON [PRIMARY]
GO

ALTER TABLE [dbo].[VirtualPos] ADD  CONSTRAINT [DF_VirtualPos_CreatedAt]  DEFAULT (getdate()) FOR [CreatedAt]
GO

ALTER TABLE [dbo].[VirtualPos] ADD  CONSTRAINT [DF_VirtualPos_Currency]  DEFAULT ((1)) FOR [Currency]
GO


`;

    autoResizeEditor(page);
    await renderSqlInput(page);
    await generate(page);
}

function debounce(fn, delay) {
    let timer = null;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}

function getSelectQueryExample() {
    return `SELECT
    P.NO Id,
    P.MIKTAR Quantity,
    P.FAIZTOPLAM / ISNULL(NULLIF(P.MIKTAR,0),1) * 1000 UnitPrice,
    P.FAIZTOPLAM Total, 
    P.DOVIZ Currency,
    P.STOKNO StockId,
    ISNULL(NULLIF(P.ExportProductName, TRIM('')), S.STOKADI) StockName,
    S.STOKKOD StockCode,
    S.DEPO WarehouseCode,
    P.MNO CustomerId,
    P.[REF] ProformaReference,
    P.[ExportPacking],
    P.[ExportDeliveryTerms],
    P.[ExportDeliveryPeriod],
    P.[ExportCountryOfOrigin],
    P.[ExportPaymentTerms],
    P.[ExportPaymentDetails],
    P.[ExportNotes],
    P.[ExportProductName],
    P.[ExportShipTo],
    P.[ExportOrderNumber],
    P.[ExportContactName],
    P.[ExportContactPhone],
    P.[SellerVatId],
    P.BANKAID PaymentBankId,
    P.CHKOD CustomerAccountCode,
    P.MADI CustomerAccountName
FROM 
    DATA_PRO P 
    JOIN DATA_STOK S ON S.NO = P.STOKNO
    LEFT JOIN DATA_BANKA B ON B.NO = P.BANKAID
WHERE 
    P.NO = @NO;`;
}

function generateDtoFromSelect(sql, options) {
    const columns = parseSelectColumns(sql);

    if (!columns.length) {
        throw new Error("SELECT column list could not be detected.");
    }

    const className = options.className || "QueryResultDto";
    const setter = options.useInitSetters ? "init;" : "set;";

    const usings = new Set();
    const lines = [];

    if (columns.some(x => x.type.includes("DateTime"))) {
        usings.add("using System;");
    }

    if (options.includeSummary) {
        lines.push("// <auto-generated>");
        lines.push("// Generated by SQL SELECT to C# DTO tool.");
        lines.push("// </auto-generated>");
        lines.push("");
    }

    if (options.nullableReferenceTypes) {
        lines.push("#nullable enable");
        lines.push("");
    }

    Array.from(usings).sort().forEach(u => lines.push(u));
    if (usings.size) lines.push("");

    if (options.namespace) {
        lines.push(`namespace ${options.namespace};`);
        lines.push("");
    }

    if (options.outputStyle === "record") {
        lines.push(`public sealed record ${className}`);
        lines.push("{");

        for (const col of columns) {
            lines.push(`    public ${col.type} ${col.name} { get; ${setter} }`);
        }

        lines.push("}");
        return lines.join("\n");
    }

    lines.push(`public sealed class ${className}`);
    lines.push("{");

    for (const col of columns) {
        lines.push(`    public ${col.type} ${col.name} { get; ${setter} }`);
    }

    lines.push("}");

    return lines.join("\n");
}

function parseSelectColumns(sql) {
    const selectBody = extractSelectBody(sql);

    if (!selectBody) {
        return [];
    }

    return splitSqlSelectList(selectBody)
        .map(part => parseSelectColumn(part))
        .filter(Boolean);
}

function extractSelectBody(sql) {
    const clean = stripSqlComments(sql)
        .replace(/\r/g, "")
        .trim();

    const selectMatch = clean.match(/\bselect\b/i);
    if (!selectMatch) return "";

    const selectStart = selectMatch.index + selectMatch[0].length;

    let depth = 0;
    let inSingleQuote = false;
    let inDoubleQuote = false;
    let inBracket = false;

    for (let i = selectStart; i < clean.length; i++) {
        const ch = clean[i];
        const next = clean[i + 1];

        if (ch === "'" && !inDoubleQuote && !inBracket) {
            if (inSingleQuote && next === "'") {
                i++;
                continue;
            }

            inSingleQuote = !inSingleQuote;
            continue;
        }

        if (ch === '"' && !inSingleQuote && !inBracket) {
            inDoubleQuote = !inDoubleQuote;
            continue;
        }

        if (ch === "[" && !inSingleQuote && !inDoubleQuote) {
            inBracket = true;
            continue;
        }

        if (ch === "]" && inBracket) {
            inBracket = false;
            continue;
        }

        if (inSingleQuote || inDoubleQuote || inBracket) continue;

        if (ch === "(") depth++;
        if (ch === ")") depth = Math.max(0, depth - 1);

        if (depth === 0 && /\bfrom\b/i.test(clean.slice(i, i + 8))) {
            return clean.slice(selectStart, i).trim();
        }
    }

    return "";
}

function splitSqlSelectList(selectBody) {
    const result = [];
    let current = "";

    let depth = 0;
    let inSingleQuote = false;
    let inDoubleQuote = false;
    let inBracket = false;

    for (let i = 0; i < selectBody.length; i++) {
        const ch = selectBody[i];
        const next = selectBody[i + 1];

        if (ch === "'" && !inDoubleQuote && !inBracket) {
            if (inSingleQuote && next === "'") {
                current += ch + next;
                i++;
                continue;
            }

            inSingleQuote = !inSingleQuote;
            current += ch;
            continue;
        }

        if (ch === '"' && !inSingleQuote && !inBracket) {
            inDoubleQuote = !inDoubleQuote;
            current += ch;
            continue;
        }

        if (ch === "[" && !inSingleQuote && !inDoubleQuote) {
            inBracket = true;
            current += ch;
            continue;
        }

        if (ch === "]" && inBracket) {
            inBracket = false;
            current += ch;
            continue;
        }

        if (!inSingleQuote && !inDoubleQuote && !inBracket) {
            if (ch === "(") depth++;
            if (ch === ")") depth = Math.max(0, depth - 1);

            if (ch === "," && depth === 0) {
                result.push(current.trim());
                current = "";
                continue;
            }
        }

        current += ch;
    }

    if (current.trim()) {
        result.push(current.trim());
    }

    return result;
}

function parseSelectColumn(expression) {
    const original = expression.trim();
    if (!original) return null;

    let alias = "";

    const asMatch = original.match(/\s+as\s+(\[[^\]]+\]|"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)\s*$/i);

    if (asMatch) {
        alias = cleanSqlIdentifier(asMatch[1]);
    } else {
        alias = extractTrailingAlias(original);
    }

    if (!alias) {
        alias = extractColumnNameWithoutAlias(original);
    }

    if (!alias) {
        return null;
    }

    const propertyName = toPascalCase(alias);

    return {
        expression: original,
        alias,
        name: propertyName,
        type: inferSelectCSharpType(alias, original)
    };
}

function extractTrailingAlias(expression) {
    const trimmed = expression.trim();

    const parts = splitExpressionByWhitespaceOutsideScopes(trimmed);

    if (parts.length < 2) {
        return "";
    }

    const possibleAlias = parts[parts.length - 1];
    const beforeAlias = trimmed.slice(0, trimmed.length - possibleAlias.length).trim();

    if (!/^(\[[^\]]+\]|"[^"]+"|[A-Za-z_][A-Za-z0-9_]*)$/.test(possibleAlias)) {
        return "";
    }

    if (!beforeAlias || beforeAlias.endsWith(".")) {
        return "";
    }

    const upper = possibleAlias.toUpperCase();
    if (["NULL", "NOT", "ASC", "DESC", "END", "ELSE", "THEN"].includes(upper)) {
        return "";
    }

    return cleanSqlIdentifier(possibleAlias);
}

function splitExpressionByWhitespaceOutsideScopes(expression) {
    const parts = [];
    let current = "";

    let depth = 0;
    let inSingleQuote = false;
    let inDoubleQuote = false;
    let inBracket = false;

    for (let i = 0; i < expression.length; i++) {
        const ch = expression[i];
        const next = expression[i + 1];

        if (ch === "'" && !inDoubleQuote && !inBracket) {
            if (inSingleQuote && next === "'") {
                current += ch + next;
                i++;
                continue;
            }

            inSingleQuote = !inSingleQuote;
            current += ch;
            continue;
        }

        if (ch === '"' && !inSingleQuote && !inBracket) {
            inDoubleQuote = !inDoubleQuote;
            current += ch;
            continue;
        }

        if (ch === "[" && !inSingleQuote && !inDoubleQuote) {
            inBracket = true;
            current += ch;
            continue;
        }

        if (ch === "]" && inBracket) {
            inBracket = false;
            current += ch;
            continue;
        }

        if (!inSingleQuote && !inDoubleQuote && !inBracket) {
            if (ch === "(") depth++;
            if (ch === ")") depth = Math.max(0, depth - 1);

            if (depth === 0 && /\s/.test(ch)) {
                if (current.trim()) {
                    parts.push(current.trim());
                    current = "";
                }

                continue;
            }
        }

        current += ch;
    }

    if (current.trim()) {
        parts.push(current.trim());
    }

    return parts;
}

function extractColumnNameWithoutAlias(expression) {
    const bracketMatch = expression.match(/(?:^|\.)(\[[^\]]+\])\s*$/);
    if (bracketMatch) {
        return cleanSqlIdentifier(bracketMatch[1]);
    }

    const plainMatch = expression.match(/(?:^|\.)([A-Za-z_][A-Za-z0-9_]*)\s*$/);
    if (plainMatch) {
        return cleanSqlIdentifier(plainMatch[1]);
    }

    return "";
}

function cleanSqlIdentifier(value) {
    return String(value || "")
        .trim()
        .replace(/^\[/, "")
        .replace(/\]$/, "")
        .replace(/^"/, "")
        .replace(/"$/, "")
        .trim();
}

function stripSqlComments(sql) {
    return String(sql || "")
        .replace(/--.*$/gm, "")
        .replace(/\/\*[\s\S]*?\*\//g, "");
}

function inferSelectCSharpType(alias, expression) {
    const name = String(alias || "").toLowerCase();
    const expr = String(expression || "").toLowerCase();

    if (/\bcount\s*\(/i.test(expression)) {
        return "int";
    }

    if (/\b(avg|sum|min|max)\s*\(/i.test(expression)) {
        return "decimal?";
    }

    if (
        name === "id" ||
        name.endsWith("id") ||
        name.endsWith("no") ||
        name === "no"
    ) {
        return name === "id" ? "int" : "int?";
    }

    if (
        name.includes("quantity") ||
        name.includes("amount") ||
        name.includes("price") ||
        name.includes("total") ||
        name.includes("rate") ||
        name.includes("balance") ||
        name.includes("miktar") ||
        name.includes("tutar") ||
        name.includes("toplam") ||
        expr.includes("/") ||
        expr.includes("*")
    ) {
        return "decimal?";
    }

    if (
        name.includes("date") ||
        name.includes("time") ||
        name.includes("created") ||
        name.includes("updated") ||
        name.includes("tarih")
    ) {
        return "DateTime?";
    }

    if (
        name.startsWith("is") ||
        name.startsWith("has") ||
        name.includes("active") ||
        name.includes("enabled")
    ) {
        return "bool?";
    }

    return "string?";
}